import { type ZapCardTemplate } from "src/components/ZapCard";
import {
  type AppImplementationResponse,
  getImplementationDetails,
} from "./getImplementationDetails";
import { searchApps } from "./search";
import { isZSLAssetWithZdl } from "src/utils/zslTypeguard";

type Step = {
  app: string;
  action: string;
};

const getAppFromStep = (
  step: Step,
  implementations: AppImplementationResponse["results"],
) => {
  return (
    implementations.find((app) => {
      return (
        app.selected_api.split("@")[0].replace("CLIAPI", "API") ===
        step.app.split("@")[0].replace("CLIAPI", "API")
      );
    }) ?? null
  );
};

export async function getZapBreakdown(template: ZapCardTemplate) {
  const isZapTemplate =
    template.products.items.length === 1 &&
    template.products.items[0].slug === "zaps";

  const firstZap = template.zsl?.zaps?.[0];
  const templateSteps: Step[] =
    isZapTemplate && firstZap && isZSLAssetWithZdl(firstZap)
      ? firstZap.zdl.steps.map((step: { app: string; action: string }) => ({
          app: step.app,
          action: step.action,
        }))
      : [];

  if (templateSteps.length === 0) return null;

  const slugs = template.apps?.items?.map((app) => app?.slug ?? "") ?? [];
  const apps = await searchApps({
    slugs: Array.from(slugs).join(","),
    limit: String(slugs.length),
  });
  const implementations = await getImplementationDetails({
    selectedApis: apps.map((app) => app.current_implementation_id),
  });

  const firstApp = getAppFromStep(templateSteps[0], implementations);
  const lastApp = getAppFromStep(
    templateSteps[templateSteps.length - 1],
    implementations,
  );

  return {
    title: template.name ?? "",
    firstStep: firstApp
      ? {
          serviceName: firstApp.name,
          serviceIcon: firstApp.images.url_16x16,
          stepLabel:
            firstApp.actions.find(
              (action) => action.key === templateSteps[0].action,
            )?.name ?? "",
        }
      : null,
    lastStep: lastApp
      ? {
          serviceName: lastApp.name,
          serviceIcon: lastApp.images.url_16x16,
          stepLabel:
            lastApp.actions.find(
              (action) =>
                action.key === templateSteps[templateSteps.length - 1].action,
            )?.name ?? "",
        }
      : null,
    totalStepCount: templateSteps.length,
    services:
      template.apps?.items?.map((app) => ({
        name: app?.name ?? "",
        src: app?.imageUrl ?? "",
      })) ?? [],
    servicesList: template.apps?.items?.map((app) => app?.name ?? "") ?? [],
  };
}
